import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import PSPDFKit from '../pspdfkit';
import {
  authenticate,
  getDocumentArrayBuffer,
  updateModifiedDate,
  loadDCIP,
  deletePage,
  getUserInformation,
  actualInsertPage
} from '../../api/legacy';
import { authenticateLegacy } from '../../api/authentication';
import {
  loadAnnotations,
  saveAnnotations,
  loadPresets,
  savePresets
} from '../../api/annotation';
import queryString from 'query-string';
import InsertModal from './insertmodal';

const baseUrl = `${window.location.protocol}//${window.location.host}/${
  process.env.PUBLIC_URL
}`;

export function LegacyViewer() {
  const params = useParams();
  const location = useLocation();
  const [presets, setPresets] = useState({});
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedInsertIndex, setSelectedInsertIndex] = useState(0);
  const [insertModalError, setInsertModalError] = useState(null);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const [pdfContent, setPdfContent] = useState(null);
  const [annotations, setAnnotations] = useState(null);
  const [token, setToken] = useState(null);
  const [isImageDoc, setIsImageDoc] = useState(false);
  const [applicationSessionId, setApplicationSessionId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePageNum, setDeletePageNum] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(
    () => {
      const loadDocument = async () => {
        const parsed = queryString.parse(location.search);
        const client = params.client;
        const documentId = params.superid;
        const appSessionId = parsed.appSessionId;
        const sessId = parsed.sessionId;
        //we are going to try and get the pdf directly and pass that to the viewer... trying to get around cors/auth issues
        //let exportPdfUrl = `https://${client}.scribonline.com/pdfexport.pdf?superid=${documentId}`;

        let annotations = null;
        let pdfContent = null;
        let tmpAnnotations = null;
        let userId = null;
        let userName = null;
        try {
          if (process.env.NODE_ENV !== 'development') {
            await authenticate(client, documentId, appSessionId, sessId);

            const response = await authenticateLegacy(
              client,
              documentId,
              appSessionId,
              sessId
            );
            const token = response.token;

            const dcip = await loadDCIP(
              client,
              documentId,
              appSessionId,
              sessId
            );
            console.log('dcip: ' + (dcip.indexOf('<DCIPDocument>') >= 0));
            const isImageDoc = dcip.indexOf('<DCIPDocument>') >= 0;

            setToken(token);
            setIsImageDoc(isImageDoc);
            setApplicationSessionId(appSessionId);
            setSessionId(sessId);

            try {
              const userInfo = await getUserInformation(
                client,
                appSessionId,
                sessId
              );
              userId = userInfo.id;
              userName = userInfo.name;
            } catch (error) {
              console.log(error);
            }
            tmpAnnotations = await loadAnnotations(token, client, documentId);

            const presetResponse = await loadPresets(token, sessId, client);

            if (presetResponse !== null && presetResponse.sessionJson) {
              setPresets(presetResponse.sessionJson.presetJson);
            }
          }
          if (process.env.NODE_ENV !== 'development') {
            console.log('In production');
            pdfContent = await getDocumentArrayBuffer(
              client,
              documentId,
              appSessionId,
              sessId
            );
          }
          if (process.env.NODE_ENV === 'development') {
            console.log('In dev using local pdf.');
            pdfContent = '/test/test.largecolor.pdf';
          }

          if (
            tmpAnnotations !== null &&
            tmpAnnotations.instantJson &&
            tmpAnnotations.instantJson.annotationJSON
          ) {
            annotations = tmpAnnotations.instantJson.annotationJSON;
            if (annotations.pdfId) {
              annotations.pdfId = null;
            }
          }

          console.log('annotations', annotations);
          setFinishedLoading(true);
          setPdfContent(pdfContent);
          setAnnotations(annotations);
          setUserId(userId);
          setUserName(userName);
        } catch (error) {
          console.log('caught error');
          console.log(error);
          setError(true);
          setErrorMessage(error);
        }
      };

      loadDocument();
    },
    [location.search, params.client, params.superid]
  );

  const internalDeletePage = pageNum => {
    // if agreed
    setShowDeleteModal(true);
    setFinishedLoading(false);
    setDeletePageNum(pageNum);
  };

  const actualDeletePage = async () => {
    const client = params.client;
    const documentId = params.superid;
    const parsed = queryString.parse(location.search);
    const appSessionId = parsed.appSessionId;
    const sessId = parsed.sessionId;
    console.log(`Going to actually delete page ${deletePageNum}`);
    await deletePage(client, documentId, deletePageNum, appSessionId, sessId);
    //on success load the pdf again
    let pdfContent = null;
    if (process.env.NODE_ENV !== 'development') {
      console.log('In production');
      pdfContent = await getDocumentArrayBuffer(
        client,
        documentId,
        appSessionId,
        sessId
      );
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('In dev using local pdf.');
      pdfContent = '/test/test.pdf';
    }
    setDeletePageNum('');
    setPdfContent(pdfContent);
    setShowDeleteModal(false);
    setFinishedLoading(true);
  };

  const toggleDeleteModal = async () => {
    const client = params.client;
    const documentId = params.superid;
    const parsed = queryString.parse(location.search);
    const appSessionId = parsed.appSessionId;
    const sessId = parsed.sessionId;
    let pdfContent = null;
    if (process.env.NODE_ENV !== 'development') {
      console.log('In production');
      pdfContent = await getDocumentArrayBuffer(
        client,
        documentId,
        appSessionId,
        sessId
      );
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('In dev using local pdf.');
      pdfContent = '/test/test.pdf';
    }
    setShowDeleteModal(prev => !prev);
    setFinishedLoading(true);
    setPdfContent(pdfContent);
  };

  const internalSavePresets = presetJson => {
    const client = params.client;
    const parsed = queryString.parse(location.search);
    const sessId = parsed.sessionId;
    savePresets(token, sessId, presetJson, client);
  };

  const internalSaveAnnotations = (instantJson, pageRotation) => {
    if (!instantJson.pageRotation) {
      instantJson.pageRotation = pageRotation;
    }
    const client = params.client;
    const documentId = params.superid;
    saveAnnotations(token, client, documentId, instantJson);
    const parsed = queryString.parse(location.search);
    const appSessionId = parsed.appSessionId;
    const sessId = parsed.sessionId;
    updateModifiedDate(client, documentId, appSessionId, sessId);
  };

  const openInsertModal = (page, totalPages) => {
    setShowInsertModal(true);
    setSelectedInsertIndex(page);
    setTotalPages(totalPages);
    setFinishedLoading(false);
  };

  const cancelInsertModal = () => {
    setShowInsertModal(false);
    setFinishedLoading(true);
  };

  const imageUploaded = files => {
    let valid = true;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log('type', file.type);
      if (
        file.type !== 'image/png' &&
        file.type !== 'image/jpg' &&
        file.type !== 'image/jpeg' &&
        file.type !== 'image/tiff' &&
        file.type !== 'image/tif'
      ) {
        valid = false;
      }
    }
    if (valid) {
      setUploadedFiles(files);
      setInsertModalError(null);
    } else {
      setUploadedFiles([]);
      setInsertModalError(
        'Invalid file type, please upload an image with type of png, jpg, or tif.'
      );
    }
  };

  const handleInsertIndexChange = event => {
    setSelectedInsertIndex(event.target.value);
  };

  const toBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const submitPageInsert = async () => {
    const client = params.client;
    const documentId = params.superid;
    const parsed = queryString.parse(location.search);
    const appSessionId = parsed.appSessionId;
    const sessId = parsed.sessionId;

    const base64urlencoded = await toBase64(uploadedFiles[0]);
    const name = uploadedFiles[0].name;
    const image = base64urlencoded.split(',')[1];
    const exportObject = {
      number: '' + selectedInsertIndex,
      name,
      image
    };
    await actualInsertPage(
      client,
      documentId,
      appSessionId,
      sessId,
      exportObject
    );
    let pdfContent = null;
    if (process.env.NODE_ENV !== 'development') {
      console.log('In production');
      pdfContent = await getDocumentArrayBuffer(
        client,
        documentId,
        appSessionId,
        sessId
      );
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('In dev using local pdf.');
      pdfContent = '/test/test.pdf';
    }
    setShowInsertModal(false);
    setFinishedLoading(true);
    setUploadedFiles([]);
    setPdfContent(pdfContent);
  };

  let licenseKey = process.env.REACT_APP_PSPDFKIT_LICENSE;
  return (
    <div className="App">
      {error && <div>{errorMessage}</div>}
      <Modal fade={false} isOpen={showDeleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Delete Page</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this page? This action cannot be
          undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={actualDeletePage}>
            Delete Page
          </Button>{' '}
          <Button color="secondary" onClick={toggleDeleteModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <InsertModal
        modal={showInsertModal}
        onCancel={cancelInsertModal}
        onConfirm={submitPageInsert}
        startPage={selectedInsertIndex}
        totalPages={totalPages}
        onPageChange={handleInsertIndexChange}
        onImageUpload={imageUploaded}
        uploadedFiles={uploadedFiles}
        error={insertModalError}
      />
      {finishedLoading && (
        <PSPDFKit
          documentId={params.superid}
          pdfUrl={pdfContent}
          licenseKey={licenseKey}
          baseUrl={baseUrl}
          instantJSON={annotations}
          saveAnnotations={internalSaveAnnotations}
          presets={presets}
          savePresets={internalSavePresets}
          showDelete={isImageDoc}
          deletePage={internalDeletePage}
          client={params.client}
          applicationSessionId={applicationSessionId}
          sessionId={sessionId}
          userId={userId}
          userName={userName}
          openInsertModal={openInsertModal}
        />
      )}
    </div>
  );
}

export default LegacyViewer;
